import axios, { AxiosResponse } from "axios";
import { ArrayFromEnvelope, configService, ObjectFromEnvelope } from "./config.service";
import {
  Employment,
  CreateEmploymentWithWorkHourPoolsPostback,
  SplitEmployment
} from "../models/employments";
import { postApiEmploymentsBulkUpdateLeaveDays } from "@/api/axios/postApiEmploymentsBulkUpdateLeaveDays";
import type { BulkUpdateLeaveDaysRequest } from "@/api/models/BulkUpdateLeaveDaysRequest";
import type { BulkUpdateLeaveDaysResponse } from "@/api/models/BulkUpdateLeaveDaysResponse";
import { convertDateOnlyToUtc } from "../utils/dateUtils";
import {
  eCalculationType,
  SupervisionTimeCalculation,
  SupervisionTimeCalculationGroup
} from "../models/supervisionTimeCalculations";
import { EmployeeTask } from "../models/task";
import { eEventBus, EventBus } from "@/eventbus";
import store from "@/store";
import { UserApiData } from "@/modules/security/models/user-model";
import { useUser } from "@/composables/apiModels/useUser";
import { addMonths } from "date-fns";
import { getQueryClient, PERIOD_STALE_TIME } from "../queryClient";

class EmploymentsService {
  async fetchAllEmployementsByEmployeeId(employeeId: number): Promise<Employment[]> {
    const user: UserApiData = store.getters["security/getUser"]();
    const { isEmployee } = useUser();
    if (isEmployee(user)) {
      return new Promise((resolve, reject) => {
        const { employmentsFrom, employmentsTo } = useUser();
        const utcFra = convertDateOnlyToUtc(employmentsFrom());
        const utcTilOgMed = convertDateOnlyToUtc(employmentsTo(user, addMonths(new Date(), 1)));
        axios
          .get<Employment[]>(configService.personaleOrAdminUrl("employments.json"), {
            params: {
              start_date: utcFra,
              end_date: utcTilOgMed,
              employee_id: employeeId
            }
          })
          .then((response: AxiosResponse) => {
            resolve(Employment.fromJSONArray(ArrayFromEnvelope<Employment>(response.data)));
          })
          .catch((error) => {
            reject(error);
          });
      });
    } else {
      return new Promise((resolve, reject) => {
        axios
          .get<Employment[]>(configService.personaleOrAdminUrl("employments.json"), {
            params: {
              employee_id: employeeId
            }
          })
          .then((response: AxiosResponse) => {
            resolve(Employment.fromJSONArray(ArrayFromEnvelope<Employment>(response.data)));
          })
          .catch((error) => {
            reject(error);
          });
      });
    }
  }

  async createEmploymentTask(employment: EmployeeTask): Promise<EmployeeTask> {
    return new Promise((resolve, reject) => {
      axios
        .post<EmployeeTask>(configService.APIInstitutionUrl("work_assignments.json"), employment)
        .then((response: AxiosResponse) => {
          resolve(EmployeeTask.fromJSON(ObjectFromEnvelope<EmployeeTask>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async updateEmploymentTask(task: EmployeeTask): Promise<EmployeeTask> {
    return new Promise((resolve, reject) => {
      axios
        .put<EmployeeTask>(
          configService.APIInstitutionUrl("work_assignments/" + task.id + ".json"),
          task.convertToPutObject()
        )
        .then((response: AxiosResponse) => {
          resolve(EmployeeTask.fromJSON(ObjectFromEnvelope<EmployeeTask>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async deleteEmploymentTask(task: EmployeeTask): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete(configService.APIInstitutionUrl("work_assignments/" + task.id + ".json"))
        .then((response: AxiosResponse) => {
          resolve(response);
        })
        .catch((error) => reject(error));
    });
  }

  async splitEmployment(obj: SplitEmployment): Promise<void> {
    return new Promise((resolve, reject) => {
      axios
        .post(configService.APIInstitutionUrl("employments/change_employment_rate.json"), obj)
        .then(() => {
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  async fetchTasks(employmentId: number): Promise<EmployeeTask[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<EmployeeTask[]>(configService.personaleOrAdminUrl("work_assignments.json"), {
          params: {
            employment_id: employmentId
          }
        })
        .then((response: AxiosResponse) => {
          resolve(EmployeeTask.fromJSONArray(ArrayFromEnvelope<EmployeeTask>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async fetchEmployments(fra: Date, tilOgMed: Date): Promise<Employment[]> {
    const utcFra = convertDateOnlyToUtc(fra);
    const utcTilOgMed = convertDateOnlyToUtc(tilOgMed);
    const institutionId = configService.institutionId();

    // "byRange" family — kept separate from the kubb [{url},params] key that
    // EmploymentTimeOverview's useGetApiEmployments uses (different shape/scope).
    const raw = await getQueryClient().fetchQuery({
      queryKey: ["employments", "byRange", institutionId, utcFra, utcTilOgMed],
      queryFn: async () => {
        const response = await axios.get<Employment[]>(
          configService.personaleOrAdminUrl("employments.json"),
          {
            params: {
              institution_id: institutionId,
              start_date: utcFra,
              end_date: utcTilOgMed
            }
          }
        );
        return ArrayFromEnvelope<Employment>(response.data);
      },
      staleTime: PERIOD_STALE_TIME
    });
    return Employment.fromJSONArray(raw);
  }

  /**
   * Employments (NO market) that the CICO/HR sync has staged a differing stop date for,
   * awaiting the manager's confirmation. The backend returns only the staged employments
   * for the institution, regardless of their active period (a previous confirmation may
   * already have moved end_date into the past).
   */
  async fetchEmploymentsWithUnconfirmedEndDate(): Promise<Employment[]> {
    const response: AxiosResponse = await axios.get<Employment[]>(
      configService.APIInstitutionUrl("employments/unconfirmed_end_dates.json")
    );
    return Employment.fromJSONArray<Employment>(
      ArrayFromEnvelope<Employment>(response.data)
    );
  }

  async fetchSupervisionTimeCalculationsByDate(
    date: Date,
    employmentId: number
  ): Promise<SupervisionTimeCalculationGroup[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<SupervisionTimeCalculationGroup[]>(
          configService.personaleOrAdminUrl(
            "supervision_time_calculations.json?employment_id=" +
              employmentId +
              "&end_date=" +
              convertDateOnlyToUtc(date)
          )
        )
        .then((response: AxiosResponse) => {
          const list: SupervisionTimeCalculationGroup[] = this.calcalationTypes(response);
          resolve(list);
        })
        .catch((error) => {
          EventBus.emit(eEventBus.BreakingDataError, {
            text: "",
            error: error
          });
          reject(error);
        });
    });
  }

  /**
   * @deprecated This method is obsolete. Use `useSupervisionCalculationsQuery` composable instead.
   *
   * The new composable provides:
   * - Smart caching with TanStack Query (15min stale time)
   * - Automatic invalidation when data changes
   * - Consistent behavior across all components
   * - Better performance through reduced API calls
   *
   * Example migration:
   * ```typescript
   * // OLD: Direct service call
   * const list = await employmentsService.fetchSupervisionTimeCalculations(employmentId);
   * const planned = list.find(c => c.calculationType === eCalculationType.Planned)?.calculation;
   *
   * // NEW: TanStack Query composable
   * const { fetchSupervisionCalculations, getPlannedCalculation } = useSupervisionCalculationsQuery();
   * const supervisionData = await fetchSupervisionCalculations(employmentId);
   * const planned = getPlannedCalculation(supervisionData);
   * ```
   *
   * @see useSupervisionCalculationsQuery
   * @todo Remove this method once all components are migrated to TanStack Query
   */
  async fetchSupervisionTimeCalculations(
    employmentId: number
  ): Promise<SupervisionTimeCalculationGroup[]> {
    // Runtime deprecation warning to help identify remaining usages
    // eslint-disable-next-line no-console
    console.warn(
      "⚠️  DEPRECATED: employmentsService.fetchSupervisionTimeCalculations() is obsolete. " +
        "Please use useSupervisionCalculationsQuery composable instead for better performance and caching."
    );

    return new Promise((resolve, reject) => {
      axios
        .get<SupervisionTimeCalculationGroup[]>(
          configService.personaleOrAdminUrl(
            "supervision_time_calculations.json?employment_id=" + employmentId
          )
        )
        .then((response: AxiosResponse) => {
          const list: SupervisionTimeCalculationGroup[] = this.calcalationTypes(response);
          resolve(list);
        })
        .catch((error) => {
          EventBus.emit(eEventBus.BreakingDataError, {
            text: "",
            error: error
          });
          reject(error);
        });
    });
  }
  private calcalationTypes(response: AxiosResponse<any>) {
    const list: SupervisionTimeCalculationGroup[] = [];
    const planned: SupervisionTimeCalculation = SupervisionTimeCalculation.fromJSON(
      response.data.data.planned
    );
    const used: SupervisionTimeCalculation = SupervisionTimeCalculation.fromJSON(
      response.data.data.used
    );
    const remaining: SupervisionTimeCalculation = SupervisionTimeCalculation.fromJSON(
      response.data.data.remaining
    );
    const status: SupervisionTimeCalculation = SupervisionTimeCalculation.fromJSON(
      response.data.data.status
    );

    list.push(new SupervisionTimeCalculationGroup(planned, eCalculationType.Planned));
    list.push(new SupervisionTimeCalculationGroup(used, eCalculationType.Used));
    list.push(new SupervisionTimeCalculationGroup(remaining, eCalculationType.Remaining));
    list.push(new SupervisionTimeCalculationGroup(status, eCalculationType.Status));
    return list;
  }

  async createEmployment(employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      axios
        .post<Employment>(
          configService.APIInstitutionUrl("employments.json"),
          employment.convertToPostObject()
        )
        .then((response: AxiosResponse) => {
          resolve(Employment.fromJSON(ObjectFromEnvelope<Employment>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }

  async createEmploymentWithWorkHourPools(
    employment: Employment,
    copy_from_employment_id: number
  ): Promise<CreateEmploymentWithWorkHourPoolsPostback> {
    return new Promise((resolve, reject) => {
      axios
        .post<Employment>(
          configService.APIInstitutionUrl("employments/create_with_work_hour_pools.json"),
          {
            employment: employment.convertToPostObject(),
            copy_from_employment_id: copy_from_employment_id
          }
        )
        .then((response: AxiosResponse) => {
          resolve(
            CreateEmploymentWithWorkHourPoolsPostback.fromJSON(
              ObjectFromEnvelope<CreateEmploymentWithWorkHourPoolsPostback>(response.data)
            )
          );
        })
        .catch((error) => reject(error));
    });
  }

  async updateEmployment(employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      axios
        .put<Employment>(
          configService.APIInstitutionUrl("employments/" + employment.id + ".json"),
          employment.convertToPutObject()
        )
        .then((response: AxiosResponse) => {
          resolve(Employment.fromJSON(ObjectFromEnvelope<Employment>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }

  async updateEmploymentSoCalculatesAs(employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      axios
        .put<Employment>(
          configService.APIInstitutionUrl(
            "employments/" + employment.id + "/update_so_calculates_as.json"
          ),
          { so_calculates_as: employment.so_calculates_as }
        )
        .then(() => {
          resolve(employment);
        })
        .catch((error) => reject(error));
    });
  }

  async bulkUpdateLeaveDays(
    request: BulkUpdateLeaveDaysRequest
  ): Promise<BulkUpdateLeaveDaysResponse> {
    return postApiEmploymentsBulkUpdateLeaveDays(request);
  }

  async deleteEmployment(employmentId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete<Employment>(
          configService.APIInstitutionUrl("employments/" + employmentId + ".json")
        )
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  }
}

export const employmentsService: EmploymentsService = new EmploymentsService();
